registerComponent("sections/sobre-secoes", function (component) {
/**
 * SOBRE-SECOES — a barra de seções do celular de "Sobre o ASA" (10/09, ADR
 * 0088): o que o _produtos.js faz pela pilha da home, para as quatro seções
 * que a barra navega aqui (valores, linha do tempo, história, escritórios).
 * O estado PRESO da barra, o header que some e volta e o select são do
 * _submenu.js, que é o mesmo das duas páginas; aqui ficam quatro coisas:
 *
 *   1. MEDIR. A posição de layout de cada seção da barra (`getBoundingClientRect`
 *      mais a rolagem, descontado o `translate` da pilha — ADR 0046) e onde o
 *      trecho termina. Um ResizeObserver remede quando uma seção muda de
 *      altura — inclusive os números, que não estão na barra mas empurram as
 *      seguintes.
 *      ⚠️ SEM `--pilha-ultima-h`. Na home a barra solta o topo quando a última
 *      seção chega nele (ADR 0019) — aqui a última são os escritórios, 3500
 *      de lista, e o arquivo desenha a barra SOBRE eles, no grafite
 *      (10805:79680). A barra fica presa até o fim do trecho e sai com a
 *      última linha dos escritórios; o header volta 200 depois (ADR 0034).
 *
 *   2. O SCROLL-SPY. `aria-current` no link da seção que está na régua (a
 *      última cujo topo já passou por ela) — é o que o select espelha — e a
 *      cor da barra pela seção atual: `sobre-secoes--escuro` no bloco quando a
 *      seção tem `data-fundo="escuro"` (os escritórios, sem painel, sobre o
 *      grafite — frame 10805:79680).
 *
 *   3. A ROLAGEM DO CLIQUE, suave, até a régua da seção (`App.scroll.scrollTo`).
 *
 *   4. A ÂNCORA NA URL, como na home (ADR 0056): o clique escreve `#secao-…`
 *      (`pushState`), o scroll-spy mantém o fragmento na seção do alto
 *      (`replaceState`) e o limpa fora do trecho; abrir a página por um link
 *      desses leva à seção pela régua (só numa navegação nova — F5 e voltar
 *      deixam a restauração do navegador mandar), e voltar/avançar no histórico
 *      rola até a seção do fragmento.
 *
 * ⚠️ SÓ ABAIXO DO DEGRAU (`Asa.desktop`): acima de 1024 a barra não existe
 * (`display: none`, _sobre-secoes.less), e um fragmento acompanhando a rolagem
 * sem barra nenhuma à vista seria ruído na URL. Cruzando o degrau para cima o
 * fragmento sai; para baixo, tudo é remedido.
 *
 * Duplica a lógica do _produtos.js de propósito (o ADR 0088 registra): a pilha
 * mede por alturas acumuladas e este bloco por rects, e mexer no script da
 * home para extrair o comum fica para quando ele for tocado.
 *
 * @param {HTMLElement} component - elemento raiz do componente
 * @param {Object} params
 */

const submenu = component.querySelector('.submenu');
const pilha = component.querySelector('.sobre-secoes__pilha');

if (submenu && pilha) {
    /* Os links da barra casados com a seção pelo fragmento do próprio href
       (`aba.hash` entrega só o "#id", mesmo com o <base href> absoluto). */
    const abas = Array.from(submenu.querySelectorAll('.submenu__link'))
        .map((aba) => ({ component: aba, painel: component.querySelector(`#${CSS.escape(aba.hash.slice(1))}`) }))
        .filter((aba) => aba.painel);

    const secoes = Array.from(pilha.children);
    const ultima = secoes[secoes.length - 1];

    const ativa = () => !Asa.desktop.matches;

    /* ---------------------------------------------------------------- medidas --
       A régua é um `calc()` de custom properties, que `getComputedStyle` não
       resolve — mas está no `scroll-margin-top` das seções, e ESSA propriedade
       vem em px. Abaixo do degrau só; acima ela é zero e nada aqui roda.

       ⚠️ UMA RÉGUA POR SEÇÃO desde 11/09 (ADR 0100). Era a da primeira, lida
       uma vez e usada por todas — e elas deixaram de ser iguais: as que passam
       POR BAIXO da barra param encostadas no fio dela (a barra presa é 16 maior
       que a caixa dela no fluxo), e a primeira, cuja cabeça é a própria barra,
       continua na régua comum. Ver _sobre-secoes.less. */
    let posicoes = [];
    let reguas = [];
    let fim = 0;

    const medir = () => {
        if (!abas.length || !ultima) return;

        /* Sem o `translate` da pilha: com o header à vista por gesto ela está
           `--header-h` abaixo do lugar, e uma medida feita no meio disso (o
           ResizeObserver dispara quando uma imagem carrega) deixaria as
           posições 64 deslocadas até a próxima. */
        const base = window.scrollY - Asa.translateY(pilha);

        posicoes = abas.map((aba) => aba.painel.getBoundingClientRect().top + base);
        reguas = abas.map((aba) => parseFloat(getComputedStyle(aba.painel).scrollMarginTop) || 0);
        fim = ultima.getBoundingClientRect().bottom + base;
    };

    /* --------------------------------------------------------- a âncora na URL --
       Sempre com o `pathname` na frente: com o `<base href>`, um `pushState('#id')`
       resolveria contra a base e não contra a página. O `try` é pelo limite do
       Chromium de 100 escritas por 30s. */
    const indiceDe = (hash) => abas.findIndex((aba) => `#${aba.painel.id}` === hash);

    const urlCom = (hash) => `${window.location.pathname}${window.location.search}${hash}`;

    const escreverHash = (indice, empilhar = false) => {
        const hash = `#${abas[indice].painel.id}`;

        if (window.location.hash === hash) return;

        try {
            window.history[empilhar ? 'pushState' : 'replaceState'](null, '', urlCom(hash));
        } catch (e) { /* limite de escritas do histórico */ }
    };

    const limparHash = () => {
        if (indiceDe(window.location.hash) < 0) return;

        try {
            window.history.replaceState(null, '', urlCom(''));
        } catch (e) { /* idem */ }
    };

    /* ------------------------------------------------------------ scroll-spy --
       A seção atual é a ÚLTIMA cujo topo de layout já passou pela régua; acima
       da primeira o destaque continua sendo o dela (`Math.max(0, …)`), e a URL
       só ganha o fragmento enquanto o trecho está na régua (`dentro`). */
    let atual = -1;
    let dentro = null;

    const marcar = (y = window.scrollY) => {
        if (!ativa() || !posicoes.length) return;

        let indice = 0;

        for (let i = 0; i < posicoes.length; i += 1) {
            if (posicoes[i] <= y + reguas[i] + 1) indice = i;
        }

        /* O trecho está na régua da PRIMEIRA seção até a base da última. */
        const limite = y + reguas[0] + 1;
        const agoraDentro = posicoes[0] <= limite && limite < fim;

        if (indice === atual && agoraDentro === dentro) return;

        if (agoraDentro) escreverHash(indice);
        else if (dentro !== null) limparHash();

        dentro = agoraDentro;

        if (indice === atual) return;
        atual = indice;

        abas.forEach((aba, i) => {
            if (i === indice) aba.component.setAttribute('aria-current', 'true');
            else aba.component.removeAttribute('aria-current');
        });

        // A cor da barra segue a superfície da seção atual (ver _sobre-secoes.less).
        component.classList.toggle('sobre-secoes--escuro', abas[indice].painel.dataset.fundo === 'escuro');
    };

    /* ------------------------------------------------------------- a rolagem --
       O clique leva a seção à régua. A URL ganha o fragmento ANTES da rolagem,
       para o histórico ter a entrada mesmo que a animação seja interrompida. */
    abas.forEach((aba, i) => {
        aba.component.addEventListener('click', (event) => {
            // Tagueamento (plano v3.1): conta também quando a barra ainda não
            // está ativa e o link segue como âncora comum.
            Asa.track('section_nav_click', {
                nav_area: 'sobre_barra',
                target_id: (aba.component.getAttribute('href') || '').split('#')[1],
                method: Asa.desktop && !Asa.desktop.matches ? 'select' : 'click',
            });

            if (!ativa() || !posicoes.length) return;

            event.preventDefault();
            escreverHash(i, true);
            App.scroll.scrollTo(posicoes[i] - reguas[i]);
        });
    });

    /* ---------------------------------------------- chegando por um link único --
       Só numa navegação NOVA: recarregar e voltar trazem o mesmo fragmento, mas
       aí o navegador restaura a rolagem de antes, e é isso que vale. Repetida a
       cada remedida enquanto a página carrega — até o `load` ou o primeiro
       gesto, o que vier antes. */
    const navegacao = /** @type {PerformanceNavigationTiming | undefined} */ (
        window.performance && performance.getEntriesByType
            ? performance.getEntriesByType('navigation')[0]
            : undefined
    );
    const restaurando = Boolean(navegacao && (navegacao.type === 'reload' || navegacao.type === 'back_forward'));

    let ancora = restaurando ? -1 : indiceDe(window.location.hash);

    const irParaAncora = () => {
        if (ancora < 0 || !ativa() || !posicoes.length) return;

        window.scrollTo({ top: posicoes[ancora] - reguas[ancora], behavior: /** @type {ScrollBehavior} */ ('instant') });
    };

    const soltarAncora = () => {
        ancora = -1;
    };

    if (ancora >= 0) {
        window.addEventListener('load', () => requestAnimationFrame(soltarAncora), { once: true });

        for (const gesto of ['wheel', 'touchstart', 'keydown', 'pointerdown']) {
            window.addEventListener(gesto, soltarAncora, { once: true, passive: true });
        }
    }

    /* Voltar e avançar no histórico (o `pushState`/`replaceState` daqui não
       disparam `hashchange`, então isto não reage ao próprio scroll-spy). */
    window.addEventListener('hashchange', () => {
        const i = indiceDe(window.location.hash);

        if (i < 0 || !ativa() || !posicoes.length) return;

        App.scroll.scrollTo(posicoes[i] - reguas[i]);
    });

    const remedir = () => {
        medir();
        atual = -1;      /* as posições mudaram: o destaque de antes não vale */
        dentro = null;
        marcar();
    };

    window.addEventListener('resize', remedir, { passive: true });

    /* Cruzando o degrau: para cima, o fragmento de seção sai da URL e a barra
       deixa de existir; para baixo, tudo é remedido e marcado. */
    Asa.desktop.addEventListener('change', (event) => {
        if (event.matches) {
            limparHash();
            atual = -1;
            dentro = null;
            return;
        }

        remedir();
    });

    medir();
    irParaAncora();

    /* Por último: `Asa.aoRolar` chama `marcar` de imediato, e as posições
       precisam estar medidas antes — e a âncora já lida da URL. */
    Asa.aoRolar(marcar);

    /* ------------------------------------------------------------- remedir --
       A altura de uma seção mexe na posição de todas as seguintes — e, se for
       a última, no alcance do `sticky` da barra. Fora do gate de movimento de
       propósito: medir é layout. */
    if (window.ResizeObserver) {
        const observador = new ResizeObserver(() => {
            medir();
            irParaAncora();
            atual = -1;
            dentro = null;
            marcar();
        });

        secoes.forEach((secao) => observador.observe(secao));
    }
}

});
